import { Store } from "bernie-plugin-mobx";
import { SerializedData } from "bernie-core";
import { action, computed, observable } from "mobx";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { getNextFriday } from "src/components/utility/DateUtils";
import {
  MapPropertySearchByIdQuery,
  MapPropertySearchByLatLongQuery,
  CustomerSearchHistoryQueryQuery,
  DateInput,
  PropertyDateRangeInput,
  CustomerPropertySearchByIdQueryQuery,
} from "__generated__/typedefs";
import { MapPlace } from "uitk-react-experimental-module-map";
import { formatText } from "bernie-l10n";
import {
  NewPriceItems,
  RecentlyViewedHotel,
  HistoryPropertyDetailsItem,
} from "src/components/flexComponents/RecentlyViewedHotels/typings";
import { CustomerPropertySearchByIdResponse } from "components/flexComponents/RecentlyViewedHotels/typings";

type StartDate = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<NonNullable<CustomerSearchHistoryQueryQuery["customer"]>["searchHistory"]>[number]
      >["items"]
    >[number]
  >["startDate"]
>;

type EndDate = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<NonNullable<CustomerSearchHistoryQueryQuery["customer"]>["searchHistory"]>[number]
      >["items"]
    >[number]
  >["endDate"]
>;

type Options = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<NonNullable<CustomerPropertySearchByIdQueryQuery["propertySearch"]>["properties"]>[number]
      >["price"]
    >["options"]
  >[number]
>;

export enum VariationType {
  PRICE_INCREASE = "increase",
  PRICE_DECREASE = "decrease",
  AVAILABLE = "available",
  SOLD_OUT = "sold_out",
}

export class HotelsStore extends Store {
  @observable
  public filteredHotels: Hotel[] | null = null;

  @observable
  public hasOverfilteredMessage: Boolean = false;

  @observable
  public allHotelsOnMap: MapPlace[] = [];

  @observable
  public isLoading = false;

  @observable
  public isLoadingFilterHotels = false;

  @observable
  public hasMapErrorMessage = false;

  public allHotelsOnMapLatLong: MapPlace[] = [];

  public constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);
  }

  /**
   * Set recently viewed hotels and hotels
   * Add infositeURL and newSearchPrice to recentViewedHotel object
   * newSearchPrice is the hotel price and originalSearchPrice is the recently view hotel price
   * @param {RecentlyViewedHotel[]} recentlyViewedHotels - Array of recently hotels viewed by the user
   * @param {CustomerPropertySearchByIdQuery.Properties} freshHotelData - Hotel that match with the recently view hotel
   * @memberof HotelsStore
   */
  public getFreshRecentlyViewedHotels = (
    recentlyViewedHotels: HistoryPropertyDetailsItem[],
    freshHotelData: CustomerPropertySearchByIdResponse
  ): RecentlyViewedHotel[] => {
    // facilitate our lives by building map of fresh hotel data with id-startDate-endDate key
    const buildDateKey = (date: StartDate | EndDate | DateInput) => `${date.year}-${date.month}-${date.day}`;
    const buildKey = (
      id: string,
      checkInDate: StartDate | EndDate | DateInput,
      checkOutDate: StartDate | EndDate | DateInput
    ) => `${id}-${buildDateKey(checkInDate)}-${buildDateKey(checkOutDate)}`;

    const { propertySearch } = freshHotelData;

    if (!propertySearch) {
      return [];
    }

    const mapOfFreshHotelData = propertySearch.properties.reduce(
      (acc2, property) => ({
        ...acc2,
        [buildKey(
          property.id,
          propertySearch.searchCriteria.dateRange.checkInDate,
          propertySearch.searchCriteria.dateRange.checkOutDate
        )]: property,
      }),
      {}
    );

    const getFreshHotel = (recentlyViewedHotel: HistoryPropertyDetailsItem) =>
      mapOfFreshHotelData[
        buildKey(
          recentlyViewedHotel.productInfo?.propertyId || "",
          recentlyViewedHotel.startDate,
          recentlyViewedHotel.endDate
        )
      ];

    return recentlyViewedHotels.filter(getFreshHotel).map((recentlyViewedHotel) => {
      // get fresh hotel
      const freshHotel = getFreshHotel(recentlyViewedHotel);

      // check for price variation
      const firstPriceOption = freshHotel.price?.options && freshHotel.price?.options[0];
      const priceVariation = this.getPriceVariation(recentlyViewedHotel, firstPriceOption);

      return {
        ...recentlyViewedHotel,
        ...priceVariation,
        hisUrl: freshHotel.infositeURL,
      };
    });
  };

  /**
   * Add a new property to recently viewed hotel's object regarding price variation
   */
  private getPriceVariation = (
    hotelFromCustomerSearchHistory: HistoryPropertyDetailsItem & NewPriceItems,
    updatedPrice?: Options | null
  ): NewPriceItems => {
    const { originalSearchPrice } = hotelFromCustomerSearchHistory;
    const originalPrice = originalSearchPrice ? Math.round(originalSearchPrice.amount) : 0;
    const currentPrice = updatedPrice?.displayPrice ? Math.round(updatedPrice.displayPrice.amount) : 0;

    // if no new pricing then it is sold out
    if (!currentPrice) {
      return {
        priceChange: VariationType.SOLD_OUT,
      };
    }

    const newPriceItem: NewPriceItems = {};

    // Is price decreased
    if (originalPrice > currentPrice) {
      newPriceItem.priceChange = VariationType.PRICE_DECREASE;
    }

    // Is increased
    if (originalPrice < currentPrice) {
      newPriceItem.priceChange = VariationType.PRICE_INCREASE;
    }

    // Is now available
    if (!originalPrice && currentPrice) {
      newPriceItem.priceChange = VariationType.AVAILABLE;
    }

    // Set new price
    if (currentPrice) {
      newPriceItem.newPrice = currentPrice;
      newPriceItem.newPriceFormatted = updatedPrice?.displayPrice?.formatted;
    }

    return newPriceItem;
  };

  @action
  public setAllHotelsOnMapOnSuccessResponse = (
    hotelsFromResponse: MapPropertySearchByIdQuery["propertySearch"]["properties"],
    locationId: string
  ) => {
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;
    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    const newHotels: MapPlace[] = hotelsFromResponse.reduce(
      (acc, newHotel) => {
        // Do not add duplicated hotels
        if (acc.find((mapPlace) => mapPlace.id === newHotel.id)) {
          return acc;
        }

        // Make sure we have a valid marker
        if (!newHotel.mapMarker || !newHotel.mapMarker.latLong || !newHotel.infositeURL) {
          return acc;
        }

        const formattedPrice = newHotel.price?.lead?.formatted;
        const priceAmount = newHotel.price?.lead?.amount;

        return [
          ...acc,
          {
            id: newHotel.id,
            placeName: newHotel.name,
            formattedPrice: priceAmount ? formattedPrice : "",
            latitude: newHotel.mapMarker.latLong.latitude,
            longitude: newHotel.mapMarker.latLong.longitude,
            overallReviewRating: formatText("maps.sheets.hotel.reviewRating.text", newHotel.reviews.score),
            reviewCount: `(${formatText(
              "maps.sheets.hotel.reviews.text",
              newHotel.reviews.total,
              newHotel.reviews.total
            )})`,
            localizedSubtitle: newHotel.reviews.localizedSubtitle,
            imageUrl: newHotel.image && newHotel.image.url,
            hrefUrl: this.constructPinnedHSRUrl(locationId, newHotel.id, checkInDate, checkOutDate),
            icon: {
              type: priceAmount ? "price" : "soldOut",
            },
          },
        ];
      },
      [...this.allHotelsOnMap]
    );

    this.allHotelsOnMap = newHotels;
  };

  @action
  public setAllHotelsOnMapOnFailureResponse = (
    hotels: Hotel[],
    contextStore: ExtendedContextStore,
    locationId: string
  ) => {
    this.allHotelsOnMap.push(...hotels.map((h) => this.mapFlexHotelToPropertyForMap(h, contextStore, locationId)));
  };

  /**
   * Fetch an array of hotels for a given latitude and longitude. Queries the
   * BEX API and reassigns the store's allHotelsOnMaps and isLoading property
   * and returns it.
   * @param {ExtendedContextStore} contextStore - Context to be passed to getBextApiContext
   * @param {number} lat - Latitude on the map that the user is looking at
   * @param {number} lng - Longitude on the map that the user is looking at
   * @returns {Promise<PropertyForMap[]>} - Array of found hotels. Defaults to empty array in case of errors
   * @memberof HotelsStore
   */
  @action
  public setAllHotelsOnMapOnHotelsByLatLongSuccessResponse(
    propertySearch: MapPropertySearchByLatLongQuery["propertySearch"],
    locationId: string
  ) {
    const newHotels: MapPlace[] = [];
    // Get search date range to determine pricing of hotels near the user
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    // Format check in and check out date to be what PropertyForMap expects
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;
    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    // Check if the hotel/property has all the necessary info to be displayed on the map
    for (const property of propertySearch.properties) {
      if (property.mapMarker && property.mapMarker.latLong && property.infositeURL) {
        const priceAmount = property.price?.lead?.amount;
        const newHotel: MapPlace = {
          id: property.id,
          placeName: property.name,
          formattedPrice:
            property.price && property.price.lead && property.price.lead.amount
              ? property.price.lead.formatted
              : undefined,
          latitude: property.mapMarker.latLong.latitude,
          longitude: property.mapMarker.latLong.longitude,
          overallReviewRating: formatText("maps.sheets.hotel.reviewRating.text", property.reviews.score),
          reviewCount: `(${formatText(
            "maps.sheets.hotel.reviews.text",
            property.reviews.total,
            property.reviews.total
          )})`,
          localizedSubtitle: property.reviews.localizedSubtitle,
          imageUrl: property.image && property.image.url,
          hrefUrl: this.constructPinnedHSRUrl(locationId, property.id, checkInDate, checkOutDate),
          icon: {
            type: priceAmount ? "price" : "soldOut",
          },
        };

        newHotels.push(newHotel);
      }
    }

    // Replace existing properties
    this.allHotelsOnMapLatLong = newHotels;
  }

  /**
   * Getter for allHotelsOnMap. Filters through them to prevent the bug on BEX
   * API/our data where we sometimes have hotels on in the middle of the ocean
   * (0,0).
   * @readonly
   * @type {MapPlace[]}
   */
  @computed
  public get hotelsOnMap(): MapPlace[] {
    return this.allHotelsOnMap.filter((hotel: MapPlace) => hotel.latitude !== 0 || hotel.longitude !== 0);
  }

  /**
   * This method converts a hotel coming from the flex composition into a hotel that can be placed on the map.
   * @param hotel A hotel coming from the composition.
   * @return a hotel for map
   */
  public mapFlexHotelToPropertyForMap(hotel: Hotel, contextStore: ExtendedContextStore, locationId: string): MapPlace {
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;

    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    const language = contextStore.locale.split(/[_-]/);

    let overallReviewRating;
    if (hotel.hotelOverallReviewRating) {
      overallReviewRating = formatText(
        "maps.sheets.hotel.reviewRating.text",
        hotel.hotelOverallReviewRating.toLocaleString(language, { maximumFractionDigits: 1 })
      );
    }

    let reviewCount;
    if (hotel.reviewCount) {
      reviewCount = `(${formatText(
        "maps.sheets.hotel.reviews.text",
        hotel.reviewCount,
        hotel.reviewCount.toLocaleString(language, { maximumFractionDigits: 1 })
      )})`;
    }

    return {
      id: hotel.hotelId,
      placeName: hotel.hotelName,
      formattedPrice: hotel.formattedPrice,
      latitude: hotel.hotelLatitude,
      longitude: hotel.hotelLongitude,
      overallReviewRating,
      reviewCount,
      imageUrl: hotel.hotelImageUrl,
      hrefUrl: this.constructPinnedHSRUrl(locationId, hotel.hotelId, checkInDate, checkOutDate),
      icon: {
        type: hotel.formattedPrice ? "price" : "soldOut",
      },
    };
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  /**
   * Calculate and return search date range for hotels we want to find around
   * our user. We choose check-in on a Friday two weeks from now. Note that the
   * range is not based on what the user input in the Wizard, so it's not dynamic
   * @returns {PropertyDateRangeInput} - Check in date and check out dates
   */
  public getSearchDateRange(): PropertyDateRangeInput {
    //Check-in is Friday two weeks from now
    //Check out is the day after that
    const today = new Date();
    const checkInDate = getNextFriday(today);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkInDate.getDate() + 1);

    return {
      checkInDate: {
        day: checkInDate.getDate(),
        month: checkInDate.getMonth() + 1,
        year: checkInDate.getFullYear(),
      },
      checkOutDate: {
        day: checkOutDate.getDate(),
        month: checkOutDate.getMonth() + 1,
        year: checkOutDate.getFullYear(),
      },
    };
  }

  public constructPinnedHSRUrl(regionId: string, hotelId: string, startDate: string, endDate: string) {
    return `/Hotel-Search?selected=${hotelId}&PinnedHotelID=${hotelId}&HadPinnedHotel=true&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}`;
  }

  public formatDate(startDate: DateInput, endDate: DateInput): PropertyDateRangeInput {
    return {
      checkInDate: {
        day: startDate.day,
        month: startDate.month,
        year: startDate.year,
      },
      checkOutDate: {
        day: endDate.day,
        month: endDate.month,
        year: endDate.year,
      },
    };
  }
}
